import { useState, useEffect } from "react";
import { api } from "@/App";
import CustomerLayout from "@/components/layout/CustomerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertCircle, Plus, Clock, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Issues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newIssue, setNewIssue] = useState({
    subject: "",
    description: "",
    related_ride_id: "",
    related_voucher_id: ""
  });
  const [rides, setRides] = useState([]);
  const [vouchers, setVouchers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [issuesRes, ridesRes, vouchersRes] = await Promise.all([
        api.get("/issues"),
        api.get("/customer/rides?limit=10"),
        api.get("/customer/vouchers")
      ]);
      setIssues(issuesRes.data);
      setRides(ridesRes.data);
      setVouchers(vouchersRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newIssue.subject || !newIssue.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/issues", {
        subject: newIssue.subject,
        description: newIssue.description,
        related_ride_id: newIssue.related_ride_id || null,
        related_voucher_id: newIssue.related_voucher_id || null
      });
      toast.success("Issue submitted successfully");
      setDialogOpen(false);
      setNewIssue({ subject: "", description: "", related_ride_id: "", related_voucher_id: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to submit issue");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'open':
        return <span className="badge badge-warning"><Clock size={12} className="mr-1" /> Open</span>;
      case 'in_progress':
        return <span className="badge badge-gold"><AlertCircle size={12} className="mr-1" /> In Progress</span>;
      case 'resolved':
        return <span className="badge badge-success"><CheckCircle size={12} className="mr-1" /> Resolved</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <CustomerLayout title="Support">
        <div className="space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="h-32 rounded-xl animate-shimmer" />
          ))}
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout title="Support">
      <div className="space-y-6 animate-fade-in">
        {/* Header with New Issue Button */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">My Issues</h2>
            <p className="text-[#A0A0A0] text-sm">Report problems or ask questions</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8942D] text-[#0A0A0B]" data-testid="new-issue-btn">
                <Plus size={18} className="mr-2" /> New Issue
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#131316] border-[#2A2A2F] text-white">
              <DialogHeader>
                <DialogTitle>Report an Issue</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#A0A0A0]">Subject *</Label>
                  <Input
                    value={newIssue.subject}
                    onChange={(e) => setNewIssue({ ...newIssue, subject: e.target.value })}
                    placeholder="Brief description of the issue"
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                    data-testid="issue-subject-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#A0A0A0]">Description *</Label>
                  <Textarea
                    value={newIssue.description}
                    onChange={(e) => setNewIssue({ ...newIssue, description: e.target.value })}
                    placeholder="Please provide details about your issue..."
                    rows={4}
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37] resize-none"
                    data-testid="issue-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A0A0A0]">Related Ride (Optional)</Label>
                    <Select
                      value={newIssue.related_ride_id}
                      onValueChange={(value) => setNewIssue({ ...newIssue, related_ride_id: value })}
                    >
                      <SelectTrigger className="bg-[#1A1A1E] border-[#2A2A2F] text-white">
                        <SelectValue placeholder="Select ride" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1E] border-[#2A2A2F]">
                        {rides.map(ride => (
                          <SelectItem key={ride.id} value={ride.id} className="text-white">
                            {ride.vehicle_type} - {formatDate(ride.ride_date)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#A0A0A0]">Related Voucher (Optional)</Label>
                    <Select
                      value={newIssue.related_voucher_id}
                      onValueChange={(value) => setNewIssue({ ...newIssue, related_voucher_id: value })}
                    >
                      <SelectTrigger className="bg-[#1A1A1E] border-[#2A2A2F] text-white">
                        <SelectValue placeholder="Select voucher" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1E] border-[#2A2A2F]">
                        {vouchers.map(voucher => (
                          <SelectItem key={voucher.id} value={voucher.id} className="text-white">
                            {voucher.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="border-[#2A2A2F] text-white hover:bg-[#1A1A1E]"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-gradient-to-r from-[#D4AF37] to-[#B8942D] text-[#0A0A0B]"
                    data-testid="submit-issue-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                    ) : (
                      'Submit Issue'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Issues List */}
        {issues.length > 0 ? (
          <div className="space-y-4">
            {issues.map((issue) => (
              <Card key={issue.id} className="bg-[#131316] border-[#2A2A2F]" data-testid={`issue-card-${issue.id}`}>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-white font-semibold">{issue.subject}</h3>
                      <p className="text-[#6B6B6B] text-sm">{formatDate(issue.created_at)}</p>
                    </div>
                    {getStatusBadge(issue.status)}
                  </div>
                  <p className="text-[#A0A0A0] text-sm mb-3">{issue.description}</p>
                  {issue.staff_notes && (
                    <div className="bg-[#0A0A0B] rounded-lg p-3 mt-3">
                      <p className="text-[#D4AF37] text-xs font-medium mb-1">Staff Response:</p>
                      <p className="text-[#A0A0A0] text-sm">{issue.staff_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <AlertCircle size={64} className="mx-auto mb-4 text-[#6B6B6B] opacity-50" />
            <h3 className="text-white text-xl font-semibold mb-2">No Issues</h3>
            <p className="text-[#A0A0A0]">Need help? Submit an issue and we&apos;ll get back to you.</p>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
};

export default Issues;
